import { useEffect, useState } from "react";
import { useGetBatchConfigs } from "../../../apis/batches/get-batch-configs";
import { useUpdateBatchConfigs } from "../../../apis/batches/update-batch-configs";

export interface IBatchConfigForm {
  MAX_PICKUP_STOPS: number;
  MAX_DELIVERY_STOPS: number;
  BATCH_WINDOW_MINUTES: number;
  MAX_PICKUP_RADIUS_MILES: number;
  MAX_TOTAL_ROUTE_MILES: number;
  MAX_SCHEDULE_WINDOW_MINUTES: number;
  SCHEDULED_DISPATCH_LEAD_MINUTES: number;
  UNASSIGNED_TIMEOUT_MINUTES: number;
  SAME_PICKUP_LOCATION_THRESHOLD_MILES: number;
}

const useBatchConfig = () => {
  const { data, isFetching: loading, refetch } = useGetBatchConfigs();
  const { mutate: updateConfigs, isPending: isUpdating } = useUpdateBatchConfigs();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<IBatchConfigForm>({
    MAX_PICKUP_STOPS: 0,
    MAX_DELIVERY_STOPS: 0,
    BATCH_WINDOW_MINUTES: 0,
    MAX_PICKUP_RADIUS_MILES: 0,
    MAX_TOTAL_ROUTE_MILES: 0,
    MAX_SCHEDULE_WINDOW_MINUTES: 0,
    SCHEDULED_DISPATCH_LEAD_MINUTES: 0,
    UNASSIGNED_TIMEOUT_MINUTES: 0,
    SAME_PICKUP_LOCATION_THRESHOLD_MILES: 0,
  });

  useEffect(() => {
    const config = data?.data;
    if (config) {
      setFormData({
        MAX_PICKUP_STOPS: config.MAX_PICKUP_STOPS ?? 0,
        MAX_DELIVERY_STOPS: config.MAX_DELIVERY_STOPS ?? 0,
        BATCH_WINDOW_MINUTES: config.BATCH_WINDOW_MINUTES ?? 0,
        MAX_PICKUP_RADIUS_MILES: config.MAX_PICKUP_RADIUS_MILES ?? 0,
        MAX_TOTAL_ROUTE_MILES: config.MAX_TOTAL_ROUTE_MILES ?? 0,
        MAX_SCHEDULE_WINDOW_MINUTES: config.MAX_SCHEDULE_WINDOW_MINUTES ?? 0,
        SCHEDULED_DISPATCH_LEAD_MINUTES: config.SCHEDULED_DISPATCH_LEAD_MINUTES ?? 0,
        UNASSIGNED_TIMEOUT_MINUTES: config.UNASSIGNED_TIMEOUT_MINUTES ?? 0,
        SAME_PICKUP_LOCATION_THRESHOLD_MILES: config.SAME_PICKUP_LOCATION_THRESHOLD_MILES ?? 0,
      });
    }
  }, [data]);

  const handleChange = (field: keyof IBatchConfigForm, value: number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const payload = {
      MAX_PICKUP_STOPS: Number(formData.MAX_PICKUP_STOPS),
      MAX_DELIVERY_STOPS: Number(formData.MAX_DELIVERY_STOPS),
      BATCH_WINDOW_MINUTES: Number(formData.BATCH_WINDOW_MINUTES),
      MAX_PICKUP_RADIUS_MILES: Number(formData.MAX_PICKUP_RADIUS_MILES),
      MAX_TOTAL_ROUTE_MILES: Number(formData.MAX_TOTAL_ROUTE_MILES),
      MAX_SCHEDULE_WINDOW_MINUTES: Number(formData.MAX_SCHEDULE_WINDOW_MINUTES),
      SCHEDULED_DISPATCH_LEAD_MINUTES: Number(formData.SCHEDULED_DISPATCH_LEAD_MINUTES),
      UNASSIGNED_TIMEOUT_MINUTES: Number(formData.UNASSIGNED_TIMEOUT_MINUTES),
      SAME_PICKUP_LOCATION_THRESHOLD_MILES: Number(formData.SAME_PICKUP_LOCATION_THRESHOLD_MILES),
    };

    updateConfigs(payload, {
      onSuccess: () => {
        setIsEditing(false);
      },
    });
  };

  return {
    loading,
    isUpdating,
    isEditing,
    setIsEditing,
    formData,
    handleChange,
    handleSubmit,
    refetch,
    rawConfig: data?.data,
  };
};

export default useBatchConfig;
